import type { Command } from 'commander';
import { existsSync, unlinkSync } from 'node:fs';
import { getConfigPath } from '../../lib/config-store';
import { InvalidArgumentError } from '../../lib/errors';

export function registerConfigReset(parent: Command): void {
	parent
		.command('reset')
		.description('Delete the config file')
		.option('-f, --force', 'skip the confirmation prompt')
		.action(async (opts: { force?: boolean }) => {
			const path = getConfigPath();
			if (!existsSync(path)) {
				process.stdout.write('No config file to delete.\n');
				return;
			}
			if (!opts.force) {
				const ok = await promptYesNo(`Delete config at ${path}? [y/N] `);
				if (!ok) {
					process.stdout.write('Aborted.\n');
					return;
				}
			}
			try {
				unlinkSync(path);
			} catch (err) {
				const e = err as NodeJS.ErrnoException;
				if (e.code === 'ENOENT') {
					// Race with another process: someone deleted it between exists & unlink.
					process.stdout.write('No config file to delete (already removed).\n');
					return;
				}
				throw new InvalidArgumentError(`Failed to delete config at ${path}: ${e.message}`);
			}
			process.stdout.write(`Deleted ${path}\n`);
		});
}

function promptYesNo(prompt: string): Promise<boolean> {
	return new Promise((resolve) => {
		process.stdout.write(prompt);
		let buf = '';
		process.stdin.setEncoding('utf8');
		const onData = (chunk: string) => {
			buf += chunk;
			const nl = buf.indexOf('\n');
			if (nl >= 0) {
				cleanup();
				const answer = buf.slice(0, nl).trim().toLowerCase();
				resolve(answer === 'y' || answer === 'yes');
			}
		};
		const onEnd = () => {
			cleanup();
			process.stdout.write('\n(no input — aborting)\n');
			resolve(false);
		};
		const cleanup = () => {
			process.stdin.removeListener('data', onData);
			process.stdin.removeListener('end', onEnd);
			process.stdin.pause();
		};
		process.stdin.on('data', onData);
		process.stdin.on('end', onEnd);
	});
}
